import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import * as organizationApi from '@/features/organization/api';
import { useActiveOrganization } from '@/features/organization/active-organization-context';
import type { OrganizationView } from '@/features/organization/types';
import type { ApiError } from '@/lib/api/client';
import { TENANT_QUERY_PREFIX } from '@/lib/api/tenant-query';

/**
 * Tenant-scoped (ADR-026 Addendum, AO-9): the query key embeds the Active
 * Organization id so Org A's cached profile cannot structurally satisfy
 * an Org B query.
 */
const organizationDetailKey = (organizationId: string | null) =>
  [TENANT_QUERY_PREFIX, organizationId, 'organization', 'detail'] as const;

/** The Active Organization's own profile (Phase G-6). Read-only - see `api.ts`. */
export function useOrganization(): UseQueryResult<OrganizationView, ApiError> {
  const { activeOrganizationId } = useActiveOrganization();
  return useQuery({
    queryKey: organizationDetailKey(activeOrganizationId),
    queryFn: () => organizationApi.getOrganization(activeOrganizationId as string),
    enabled: activeOrganizationId !== null,
  });
}
